/**
 * @fileoverview Report Logs API Service
 * @description API calls for the report logs SFTP feature
 */

import { apiClient } from '@/services';
import { APP_CONFIG } from '@/config';

const BASE = '/v1/report-logs';

export const reportLogsApi = {
  /**
   * Health check
   */
  health: () => apiClient.get(`${BASE}/health`),

  /**
   * List files/directories at a remote SFTP path
   * @param {string} [path='/'] - Remote directory path
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  listDirectory: (path = '/', env = 'test') => apiClient.get(`${BASE}/list`, { path, env }),

  /**
   * Fetch content of a specific remote file
   * @param {string} path - Full remote file path
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  fetchFile: (path, env = 'test') => apiClient.get(`${BASE}/fetch`, { path, env }),

  /**
   * Search for log files matching a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} [basePath='/'] - Base directory to search from
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  searchByDate: (date, basePath = '/', env = 'test') =>
    apiClient.get(`${BASE}/search`, { date, base_path: basePath, env }),

  /**
   * Get available report types
   */
  getReportTypes: () => apiClient.get(`${BASE}/reports`),

  /**
   * Execute a report generation over an inclusive date range
   * @param {string} reportId - Report type ID
   * @param {string} startDate - Range start in YYYY-MM-DD format
   * @param {string} endDate - Range end in YYYY-MM-DD format
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  executeReport: (reportId, startDate, endDate, env = 'test') =>
    apiClient.post(`${BASE}/reports/execute`, {
      report_id: reportId,
      start_date: startDate,
      end_date: endDate,
      env,
    }),

  /**
   * Download a report file (authenticated fetch → blob)
   * @param {string} path - Remote file path
   * @param {string} [filename] - Suggested download filename
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  downloadReport: (path, filename, env = 'test') => {
    const params = new URLSearchParams({ path, env });
    if (filename) params.append('filename', filename);
    return apiClient.getRaw(`${BASE}/reports/download?${params.toString()}`);
  },

  /**
   * Get all available data sources with date ranges
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  getDataSources: (env = 'test') => apiClient.get(`${BASE}/sources`, { env }),

  /**
   * List files for a specific data source and date
   * @param {string} sourceId - Data source folder name
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  getSourceFiles: (sourceId, date, env = 'test') =>
    apiClient.get(`${BASE}/sources/${sourceId}/dates/${date}`, { env }),

  /**
   * Search text content in files at a given path
   * @param {string} query - Text to search for
   * @param {string} path - Folder path to search in
   * @param {number} [maxResults=100] - Max matching lines
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  searchText: (query, path, maxResults = 100, env = 'test') =>
    apiClient.get(`${BASE}/search-text`, { q: query, path, max_results: maxResults, env }),

  /**
   * AI-powered natural language log search
   * @param {string} query - Natural language search query
   * @param {boolean} [summarize=true] - Whether to include AI summary
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  aiSearch: (query, summarize = true, env = 'test') =>
    apiClient.post(`${BASE}/ai-search`, { query, summarize, env }),

  /**
   * Generate a consolidated CSV report from AI search results
   * @param {object} searchData - Object with results, query, searched_date, searched_folders
   * @param {string} [env='test'] - SFTP environment ('test' or 'cert')
   */
  generateAiReport: (searchData, env = 'test') =>
    apiClient.post(`${BASE}/ai-search/generate-report`, { ...searchData, env }),
};
